import { Typography, Box, Grid, Paper } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { PageContainer, SectionContainer, SectionHeader, BodyText } from '../styles/components';

function SEO() {
  // JSON-LD structured data for the wedding event
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Sam Musso and Alexa Ballenger Wedding",
    "startDate": "2025-08-02",
    "location": {
      "@type": "Place",
      "name": "Colorado",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "CO",
        "addressCountry": "US"
      }
    },
    "description": "Join us for the wedding celebration of Sam Musso and Alexa Ballenger in Colorado",
    "organizer": {
      "@type": "Person",
      "name": "Sam Musso and Alexa Ballenger"
    },
    "url": "https://www.happilyevermoose.com",
    "image": "https://www.happilyevermoose.com/echo_mountain.jpg"
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Sam Musso & Alexa Ballenger Wedding | August 2025 | Colorado</title>
        <meta name="description" content="Join us for the wedding of Sam Musso and Alexa Ballenger in August 2025 in Colorado. Visit happilyevermoose.com for more information about our special day." />
        <meta name="keywords" content="Sam Musso, Alexa Ballenger, wedding, Colorado wedding, Evergreen wedding, August 2025, mountain wedding, happilyevermoose, happily ever moose, moose, musso" />
        <meta property="og:title" content="Sam Musso & Alexa Ballenger Wedding" />
        <meta property="og:description" content="Sam Musso and Alexa Ballenger are getting married in August 2025 in Colorado. Join us for our special day!" />
        <meta property="og:image" content="/echo_mountain.jpg" />
        <meta property="og:url" content="https://www.happilyevermoose.com" />
        <meta property="og:site_name" content="Sam & Alexa's Wedding" />
        <link rel="canonical" href="https://www.happilyevermoose.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <SectionContainer>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h2" 
            component="h1"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 700,
            }}
          >
            Sam Musso & Alexa Ballenger Wedding
          </Typography>
          
          <Typography
            variant="h4"
            component="p"
            align="center"
            sx={{ mb: 2 }}
          >
            August 2025 • Colorado
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <SectionHeader>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Our Story
                </Typography>
              </SectionHeader>
              
              <BodyText>
                Alexa and Sam met in Boulder, Colorado, when Alexa was a senior at CU Boulder and Sam had just moved from Pittsburgh to pursue career opportunities in software engineering.
              </BodyText>

              <BodyText>
                Over time, they slowly became friends, drawn together by a shared sense of humor and a surprising number of common interests. During that time, Alexa introduced Sam to the beauty of the outdoors, while Sam (literally) helped Alexa conquer math so she could pass her chemistry final—with a solid B- and a big sigh of relief.
              </BodyText>

              <BodyText>
                To the surprise of no one around them, their friendship blossomed into something more.
              </BodyText>

              <BodyText>
                Since then, they've circumnavigated the globe together, rebuilt a home (ask Sam about his retaining wall), and raised two very cute dogs (shout out to the Haze Beast & Rocket Boi!). Along the way, Alexa has shown Sam how to take life just a touch less seriously, while Sam has been a steady hand, helping Alexa build a career she loves. They've become each other's biggest fans, anchors, and adventure partners.
              </BodyText>

              <BodyText>
                Now, nearly eight years later, they're getting married—excited, grateful, and more in love than ever as they take this next step together.
              </BodyText>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <SectionHeader>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Wedding Details
                </Typography>
              </SectionHeader>
              
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                Date
              </Typography>
              <BodyText>
                August 2025<br />
                Colorado
              </BodyText>
              
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                About Our Wedding
              </Typography>
              <BodyText>
                We're excited to celebrate our special day in the beautiful mountains of Colorado. 
                Specific details about the venue, timing, and directions are available to invited guests 
                through our password-protected wedding website.
              </BodyText>
              
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                For Our Guests
              </Typography>
              <BodyText>
                If you've received an invitation, you can access all the wedding details including 
                accommodations, registry information, and RSVP options by visiting our website and 
                entering the password provided on your invitation.
              </BodyText>
              
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                Registry
              </Typography>
              <BodyText>
                Your presence at our wedding is the greatest gift. However, if you wish to honor us with a gift, we have created a registry for your convenience.
              </BodyText>
            </Paper>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1">
            For more information and to RSVP, please visit our website using the password provided on your invitation.
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
            www.happilyevermoose.com
          </Typography>
        </Box>
      </SectionContainer>
    </PageContainer>
  );
}

export default SEO;
